import React, { useState, useEffect } from "react";
import {
  FaUser,
  FaArrowLeft,
  FaEllipsisV,
  FaFilter,
  FaPlus,
  FaChevronDown,
  FaSortAmountDown,
  FaSortAmountUp,
} from "react-icons/fa";
import { BASE_URL } from "utils/constant";
import DeliveryCard from "../Delivery/DeliveryCard";
import MaterialPurchaseForm from "../../Finance/MaterialPurchaseForm";
import MaterialPurchaseDetails from "../Delivery/MaterialPurchaseDetails";
import { useAlert } from "components/UI_components/Alert";
import EditVendorModal from "./EditVendorModal";

const VendorProfile = ({ vendor, onBack, userPermissions, setTab, setDelivery }) => {
  const [deliveries, setDeliveries] = useState([]);
  const [vendorDetails, setVendorDetails] = useState(vendor);
  const [sortedDeliveries, setSortedDeliveries] = useState([]);

  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [showNewDeliveryForm, setShowNewDeliveryForm] = useState(false);
  const [showMaterialPurchaseDetails, setShowMaterialPurchaseDetails] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [showEditVendorModal, setShowEditVendorModal] = useState(false);
  
  // Sort states
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [sortBy, setSortBy] = useState('created_on');
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'

  const alert = useAlert();
  const itemsPerPage = 5;

  // Sort options configuration
  const sortOptions = [
    { value: 'created_on', label: 'Created Date' },
    { value: 'date', label: 'Delivery Date' },
    { value: 'paid_amount', label: 'Total Paid' },
    { value: 'total_price', label: 'Total Price' },
    { value: 'remaining_payment', label: 'Remaining Amount' },
  ];

  useEffect(() => {
    fetchVendorDeliveries();
  }, []);

  useEffect(() => {
    sortDeliveries();
  }, [deliveries, sortBy, sortOrder]);

  const fetchVendorDeliveries = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      const site = localStorage.getItem("site");

      const response = await fetch(
        `${BASE_URL}/inventory/list-vendor-deliveries/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken.trim()}`,
          },
          body: JSON.stringify({
            sites: site,
            vendor: vendor.id,
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to fetch deliveries");

      const result = await response.json();
      if (result.status && Array.isArray(result.data)) {
        setDeliveries(result.data);
      }
    } catch (error) {
      console.error("Error fetching vendor deliveries:", error);
    } finally {
      setLoading(false);
    }
  };

  const sortDeliveries = () => {
    const sorted = [...deliveries].sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'created_on':
          aValue = new Date(a.created_on || a.created_at || 0);
          bValue = new Date(b.created_on || b.created_at || 0);
          break;
        case 'date':
          aValue = new Date(a.date || 0);
          bValue = new Date(b.date || 0);
          break;
        case 'paid_amount':
          aValue = a.paid_amount || 0;
          bValue = b.paid_amount || 0;
          break;
        case 'total_price':
          aValue = a.total_price || 0;
          bValue = b.total_price || 0;
          break;
        case 'remaining_payment':
          aValue = (a.total_price || 0) - (a.paid_amount || 0);
          bValue = (b.total_price || 0) - (b.paid_amount || 0);
          break;
        default:
          aValue = a.created_on || a.created_at || 0;
          bValue = b.created_on || b.created_at || 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    setSortedDeliveries(sorted);
  };

  const handleSortChange = (newSortBy) => {
    if (newSortBy === sortBy) {
      // If clicking the same sort option, toggle order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a different sort option, set new sort and default to desc
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
    setShowSortDropdown(false);
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const calculatePayments = () => {
    return deliveries.reduce(
      (acc, delivery) => {
        acc.totalPayment += delivery.paid_amount || 0;
        acc.pendingPayment += delivery.total_price - delivery.paid_amount || 0;
        acc.refundedAmount += delivery.refunded_amount || 0;
        return acc;
      },
      {
        totalPayment: 0,
        pendingPayment: 0,
        refundedAmount: 0,
      }
    );
  };

  const payments = calculatePayments();

  const paginatedDeliveries = sortedDeliveries.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getCurrentSortLabel = () => {
    const option = sortOptions.find(opt => opt.value === sortBy);
    return option ? option.label : 'Created Date';
  };

  if (showEditVendorModal) {
    return (
      <EditVendorModal
        isOpen={showEditVendorModal}
        vendor={vendorDetails}
        onClose={() => setShowEditVendorModal(false)}
        onUpdate={(updated) => setVendorDetails((prev) => ({ ...prev, ...updated }))}
      />
    );
  }

  if (showNewDeliveryForm) {
    return (
      <MaterialPurchaseForm
        vendor={vendor}
        onClose={() => {
          setShowNewDeliveryForm(false);
          fetchVendorDeliveries();
        }}
      />
    );
  }

  if (showMaterialPurchaseDetails) {
    return (
      <MaterialPurchaseDetails
        Delivery={selectedDelivery}
        onClose={() => {
          setShowMaterialPurchaseDetails(false);
          fetchVendorDeliveries();
          setSelectedDelivery(null);
        }}
        setTab={setTab}
        setDelivery={setDelivery}
        userPermissions={userPermissions}
      />
    );
  }

  return (
    <div className="bg-gray-100 h-full">
      {/* Header */}
      <div className="bg-white px-4 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <FaArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-semibold">Vendor/Shop's Profile</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="p-2 hover:bg-gray-100 rounded-full"
            onClick={() => setShowEditVendorModal(true)}
          >
            <FaEllipsisV size={20} />
          </button>
        </div>
      </div>
      <div
        className="h-[90%] flex flex-col"
        style={{
          overflowY: "auto",
        }}
      >
        {/* Vendor Info Card */}
        <div className="m-4 bg-white rounded-lg shadow-sm">
          <div className="p-4 border-b">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-100 rounded-full">
                <FaUser size={24} className="text-purple-700" />
              </div>
              <div>
                <h2 className="font-semibold">{vendorDetails.name}</h2>
                <span className="text-sm text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
                  {vendorDetails.vendor_type}
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Contact No: </span>
                +91-{vendorDetails.contact_no}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Address: </span>
                {vendorDetails.address || "no data"}
              </p>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="p-4 grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Total Payment</p>
              <p className="font-semibold">
                ₹{payments.totalPayment.toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Pending Payment</p>
              <p className="font-semibold text-red-500">
                ₹{payments.pendingPayment.toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Advance Payment</p>
              <p className="font-semibold">₹0</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Refunded Amount</p>
              <p className="font-semibold">
                ₹{payments.refundedAmount.toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Deliveries Section */}
        <div className="m-4">
          <div className="flex items-center justify-between mb-4">
            <p className="font-medium">{deliveries.length} Deliveries</p>
            <div className="flex items-center gap-2">
              {/* Sort Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  <span className="text-sm">Sort by {getCurrentSortLabel()}</span>
                  {sortOrder === 'asc' ? (
                    <FaSortAmountUp size={14} />
                  ) : (
                    <FaSortAmountDown size={14} />
                  )}
                  <FaChevronDown size={12} className={`transition-transform ${showSortDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {showSortDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                    <div className="py-1">
                      {sortOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleSortChange(option.value)}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${
                            sortBy === option.value ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                          }`}
                        >
                          <span>{option.label}</span>
                          {sortBy === option.value && (
                            sortOrder === 'asc' ? (
                              <FaSortAmountUp size={12} />
                            ) : (
                              <FaSortAmountDown size={12} />
                            )
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={() => {
                  if (
                    !userPermissions.edit_material_management &&
                    !userPermissions.edit_all &&
                    !userPermissions?.is_admin
                  ) {
                    alert.addAlert(
                      "You don't have permission to perform this action. Contact your admin to give you edit material management permissions",
                      "error"
                    );
                    return;
                  }
                  setShowNewDeliveryForm(true);
                }}
                className="flex items-center gap-2 px-4 py-1.5 bg-text-clr text-white rounded-md hover:bg-purple-800"
              >
                <FaPlus size={16} />
                <span>Purchase</span>
              </button>
            </div>
          </div>

          {/* Delivery Cards */}
          <div className="space-y-4">
            {loading ? (
              <p>Loading deliveries...</p>
            ) : (
              paginatedDeliveries.map((delivery) => (
                <DeliveryCard
                  key={delivery.id}
                  delivery={delivery}
                  onClick={() => {
                    setSelectedDelivery(delivery);
                    setShowMaterialPurchaseDetails(true);
                  }}
                />
              ))
            )}
          </div>

          {/* Pagination */}
          <div className="flex justify-center gap-2 mt-4">
            {Array.from({
              length: Math.ceil(sortedDeliveries.length / itemsPerPage),
            }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPage(index + 1)}
                className={`px-3 py-1 rounded ${
                  currentPage === index + 1
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100"
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorProfile;